import {IQueuedPdfServerJob, IServerPDFQueryPayload, IServerPDFReportJobPayload} from '@modules/pdf'
import {DashboardQueryParams, defaultReport, globalStores, ICatalogUserSettings, IUser, nav, ReportCatalogSerializableSettings, ui} from '@stores'
import {ICatalogReportEs, DEBUG_REPORT_SAVE, reportSettingsPayloadOmitFields, reportSettingFieldsToSerialize} from './ReportsStore'
import {diff} from 'jsondiffpatch'
import {action, computed, observable, runInAction, toJS, when, _, stringify, lockr} from '@api'
import { guid } from '@utils'

export class ReportWrapper {
  @observable.ref navigatedFromDraft: ReportWrapper

  @observable isLocalDraft = false

  /**
   * When we submit a PDF server job we need all tabs to be mounted so we can use things like {@see GroupByQueryManagerStore}
   */
  @observable aboutToSubmitPdfJob = false
  @observable serverPdfJob: IQueuedPdfServerJob


  @computed get localStorageDraftId() {
    return `report-draft-${this.id}`
  }

  @computed get creator(): Partial<IUser> {
    return this.store.stores.users.getById(this.report.creator)
  }

  @computed get isDraft() { return this.report.draftedFrom?.id != null}

  get store() { return globalStores.reports}

  @computed get reportJsonForCopy() {
    const {report} = this
    const r = {
      settings: report.settings,
      title: `Copy of ${report.title ?? 'Report'}`,
      forkedFrom: {id: report.id, version: report.version}
    } as ICatalogReportEs

    return r
  }

  @observable saving = false
  @observable report: ICatalogReportEs

  /**
   * Show the edit report dialog modal?
   */
  @observable isEditing = false
  @action onEditReportDetails = () => this.isEditing = true
  @action onStopEditing = () => this.isEditing = false
  @observable isEditingPDFDetails = false
  @action onEditReportPDFDetails = () => {
    this.isEditingPDFDetails = true

    console.log(this.store.currentReportPDFPayload)
    this.store.stores.cloudPDF.submitCurrentReportPDFJob()
  }
  @action onStopEditingPDF = () => this.isEditingPDFDetails = false

  constructor(public _originalReport: ICatalogReportEs, public originalSettings?: ICatalogUserSettings) {
    const report = _.merge({}, defaultReport, _originalReport),
      {store} = this

    this.report = report

    if (!this.id) {
      debugger
    }

    store.reportById.set(this.id, this)

    // try {
    //   this.originalSettings = originalSettings ?? (!report.settings ? removeUndefinedsNested(JSON.parse(report.settings)) : {})
    // } catch (err) {
    //   this.originalSettings = {}
    // }

    // Load our drafted from report if not already loaded
    if (ui.settings.reports.draftReports && this.isDraft) {
      setTimeout(() => {
        if (!store.reportById.has(report.draftedFrom.id)) {
          DEBUG_REPORT_SAVE && console.log(`Load external report - ${report.draftedFrom.id}`)
          store.getReport({id: report.draftedFrom.id})
        }
      }, 50)
    }
  }

  @action toggleShowOriginal = () => {
    const {isDraft, report: r, store} = this

    if (isDraft) {
      when(() => this.originalReport != null, () => {
        this.originalReport?.navigateTo()
        this.originalReport.navigatedFromDraft = this
      })
    } else {
      this.navigatedFromDraft?.navigateTo()
    }
  }

  @computed get isMyReport() {
    const {store: {reportUserId}, report: {creator}} = this
    return (creator === reportUserId)
  }

  get isPublic() {
    return this.report.isPublic
  }

  @action toggleIsPublic = async () => {
    if (this.isMyReport) {
      this.report.isPublic = !this.report.isPublic
      await this.saveChanges()
    }
  }

  get canDelete() {
    return this.isMyReport
  }

  get canSave() {
    return this.isMyReport
  }

  @action cloneReport = async (navigateAfter = true, overrides?: Partial<ICatalogReportEs>) => {
    const {report, store: {reportUserId, es, flexLayout}, store} = this
    // Clone the report
    DEBUG_REPORT_SAVE && console.debug(`Cloning report ${report.id}`)

    const body = {
      ..._.omit(report, ['_id', 'originalSettings']),
      modifiedDate: new Date(),
      draftedFrom: null,
      forkedFrom: {
        id: report._id,
        version: report.version
      },
      creator: reportUserId,
      version: 1,
      title: `Copy of ${report.title}`,
      ...(overrides ?? {})
    } as Partial<ICatalogReportEs>

    const newReportRaw = await es.genericUpsert({
      index: es.indexes.reports,
      refresh: true,
      body
    })

    return runInAction(() => {
      const newReport = new ReportWrapper(newReportRaw.data)
      DEBUG_REPORT_SAVE && console.debug(`Cloned report:`, {original: report, newReport})
      flexLayout._updatingSettings = true

      if (navigateAfter) {
        store.currentReport = newReport
        store.updateReportQueryParams()
      }

      store.loadRecentReports()

      this.saving = false
      return newReport
    })
  }

  @action discardDraft = async (navigateAfter = true) => {
    const {isDraft, report: {id}, isCurrentReport, store} = this
    if (!isDraft) throw new Error(`Report '${id}' must be in a draft state`)
    if (!this.isMyReport) throw new Error(`Report '${id}} is not editable by you`)

    if (isCurrentReport && navigateAfter) {  // We can also delete drafts from the file->open menu
      if (this.originalReport) {
        DEBUG_REPORT_SAVE && console.log(`Discarding draft and navigating to original report...`)
        store.justDiscardedDraft = true
        store.currentReportModel = null
        this.originalReport.navigateTo()
      } else {
        debugger
      }
    }

    await this.deleteReport(true)
  }

  @action publishDraft = () => {
    const {store: {es, catalog, reportUserId}} = this
    when(() => this.originalReport !== null, async () => {
      const {originalReport: {report: originalReport}, report, store, store: {settingsStringified}} = this
      DEBUG_REPORT_SAVE && console.debug(`Publishing draft`, toJS({originalReport, report}, {recurseEverything: true}))
      const r = await es.genericUpsert<ICatalogReportEs>({
        index: es.indexes.reports,
        _id: report.draftedFrom.id,
        body: {
          ...defaultReport,
          ..._.omit(report ?? {}, ['_id', 'id', 'originalSettings']),
          title: report.title,
          isDraft: false,
          draftedFrom: {},
          settings: settingsStringified,
          modifiedDate: new Date(),
          version: originalReport.version + 1
        } as Partial<ICatalogReportEs>
      })

      runInAction(async () => {
        DEBUG_REPORT_SAVE && console.debug(`Draft published:`, {r})
        store.justPublishedDraft = true
        store.currentReport = new ReportWrapper(r.data)

        await this.discardDraft(false)

        const {currentReport} = store
        await currentReport.navigateTo()

        await store.loadRecentReports()
      })
    })
  }

  @action saveCopy = () => {
    this.store.createReport(true, this)
  }

  get slug() { return this.report.slug }

  @computed get id() {
    return this.store.catalog.idFor(this.report)
  }

  @computed.struct get queryParams(): DashboardQueryParams {
    const {slug, id} = this

    return slug ? {report: slug, id: undefined} : {id, report: undefined}
  }

  //@observable.ref originalSettings: ICatalogUserSettings

  @computed get isCurrentReport() {
    const {report: {id, slug}, store: {stores: {nav: {dashboardQueryParams: queryParams}}}} = this
    if ((queryParams.id && id === queryParams.id) || (queryParams.report && slug === queryParams.report && !this.isDraft)) return true
    return this.id === this.store.currentReport?.id
  }

  @action deleteReport = async (deleteOnlyDraft = false) => {
    const {isCurrentReport, store, isDraft, originalReport, store: {es, getReport}, id} = this

    if (deleteOnlyDraft && !isDraft) {
      throw new Error(`deleteReport() called with deleteOnlyDraft but report is not in draft state`)
    }

    await es.delete(id, es.indexes.reports)

    await runInAction(async () => {
      store.recentReports = store.recentReports.filter(r => r.id !== id)
      store.reportById.delete(id)
      if (!deleteOnlyDraft && isDraft && originalReport) {
        await originalReport.deleteReport()
      }

      if (isCurrentReport) {
        store.currentReport = null
        store.currentReportModel = null

        const myFirstReport = store.recentReports.find(r => r.isMyReport)

        if (myFirstReport) {
          await myFirstReport.navigateTo()
        } else {
          await store.createReport(true)
        }
      }

      await store.loadRecentReports()
    })
  }

  // updateSettings = _.debounce(
  //   async (settings: ICatalogUserSettings, draftReportConfig: Partial<ICatalogReportEs>) => this._updateSettings(settings, draftReportConfig),
  //   1000 // Needs to be long enough to avoid version conflicts in ES
  //   //ui.settings.reports.updateSettingsDebounce
  // )

  @observable originalSettingsJson : string

  @action discardLocalChanges = async () => {
    lockr.rm(this.localStorageDraftId)
    this.report.settings = this.originalSettingsJson
    this.isLocalDraft = false
    this.navigateTo()
  }

  @action saveChanges = async () => {
    const {store: {catalog: {settingsStore}}} = this

    const settings = _.pick(settingsStore.reportSettings, reportSettingFieldsToSerialize)

    this.store._needsDirtyUpdate = false
    await this.updateSettings(settings)
  }

  @action updateSettings = async (settings: ReportCatalogSerializableSettings, draftReportConfig: Partial<ICatalogReportEs> = null, refresh = true) => {
    // console.debug(`Skipping save due to 500 error in server`)
    // r
    // eturn

    DEBUG_REPORT_SAVE && console.debug(`updateSettings`, {settings, draftReportConfig, refresh, saving: this.saving})

    if (this.saving) {
      when(() => !this.saving, () => {
        if (!this.store.justLoadedReport) {
          this.updateSettings(settings, draftReportConfig, refresh)
        }
      })
      return
    }

    this.saving = true

    try {
      //todo - add a mutex/lock/cancellable to avoid upsert conflict errors
      const {report, isDraft, isMyReport, canSave, store, store: {es, reportUserId, currentReport, flexLayout, stores: {ui: {settings: {reports: {draftReports}}}}}, id} = this,
        settingsToSerialize = _.pick(settings, reportSettingFieldsToSerialize),
        settingsStringified = stringify(settingsToSerialize)

      if (report && !draftReportConfig) {
        const settingsDelta = diff(report.settings, _.isString(report.settings) ? settingsStringified : settings)

        if (!settingsDelta) {
          DEBUG_REPORT_SAVE && console.debug(`No diff detected, bailing from updateSettings()`, settingsDelta)
          return
        }

        if (DEBUG_REPORT_SAVE) {
          const dbSettings = _.isString(report.settings) ? JSON.parse(report.settings) : report.settings

          console.debug(`Detected change in settings, updating our report: `, {settingsDelta, dbSettings, settingsToSerialize, diff: diff(dbSettings, settingsToSerialize)})
        }
      }

      if (!settings.flexLayout?.model) {
        debugger
      }

      if (!draftReports) {
        try {
          if (!isMyReport) {
            // Clone the report
            DEBUG_REPORT_SAVE && console.debug(`Cloning report ${id}`)

            const body = {
              ..._.omit(report, ['_id', 'originalSettings']),
              ..._.omit(draftReportConfig ?? {}, reportSettingsPayloadOmitFields),
              settings: settingsStringified,
              title: `Copy of ${report.title ?? 'Report'}`,
              modifiedDate: new Date(),
              draftedFrom: null,
              forkedFrom: {
                id: report._id,
                version: report.version
              },
              creator: reportUserId,
              version: 1,
              createdDate: new Date(),
              slug: undefined
            } as Partial<ICatalogReportEs>

            const newReportRaw = await es.genericUpsert({
              index: es.indexes.reports,
              refresh,
              body
            })

            runInAction(() => {
              const newReport = new ReportWrapper(newReportRaw.data)
              DEBUG_REPORT_SAVE && console.debug(`Cloned report:`, {original: report, newReport})
              flexLayout._updatingSettings = true
              store.currentReport = newReport
              store.updateReportQueryParams()

              store.loadRecentReports()

              this.saving = false
            })

            return newReportRaw.data
          } else {
            DEBUG_REPORT_SAVE && console.debug(`Saving changes to report ${id}`)

            const body = {
              ..._.omit(report, ['_id', 'originalSettings']),
              ..._.omit(draftReportConfig ?? {}, reportSettingsPayloadOmitFields),
              settings: settingsStringified,
              version: report.version + 1
            } as Partial<ICatalogReportEs>

            const r = await es.genericUpsert({
              index: es.indexes.reports,
              _id: id,
              refresh,
              body
            })

            runInAction(() => {
              let original = DEBUG_REPORT_SAVE ? _.cloneDeep(this.report) : null
              if (refresh) {
                _.assign(this.report, refresh ? r.data : body)
              }

              // this.originalSettings = removeUndefinedsNested(_.cloneDeep(settings))

              store.updateReportQueryParams()
              DEBUG_REPORT_SAVE && console.debug(`Settings updated in ES for report ${r.data.id}`, {responseDataSubjectToRefresh: r.data, refresh, delta: diff(original, report)})
              this.saving = false
            })

            return r.data
          }
        } finally {
          runInAction(() => {
            flexLayout._updatingSettings = true
            this.saving = false
          })
        }
      } else {
        if (!isDraft || !isMyReport) {
          // Create a draft version of the report
          DEBUG_REPORT_SAVE && console.debug(`Creating draft copy of report ${currentReport.id}...`)

          const newReportBody = {
              ..._.omit(report, reportSettingsPayloadOmitFields),
              ..._.omit(draftReportConfig ?? {}, reportSettingsPayloadOmitFields),
              settings: settingsStringified,
              modifiedDate: new Date(),
              createdDate: new Date(),
              slug: undefined,
              creator: reportUserId,
              version: 1,
              draftedFrom: {id: report.draftedFrom?.id ?? id, version: report.version}
            } as ICatalogReportEs,
            newReport = await es.genericUpsert<ICatalogReportEs>({
              index: es.indexes.reports,
              body: newReportBody
            })

          runInAction(() => {
            DEBUG_REPORT_SAVE && console.debug(`Draft report:`, newReport.data)
            const draftReport = new ReportWrapper(newReport.data)
            flexLayout._updatingSettings = true
            store.currentReport = draftReport
            store.recentReports = store.recentReports.filter(r => r.id != id)
            store.recentReports.push(draftReport)
            store.updateReportQueryParams()
            this.saving = false
            DEBUG_REPORT_SAVE && console.debug(`Settings updated for DRAFT report ${draftReport.id}`)
          })

          return newReport.data
        } else if (isMyReport) {
          // Update

          DEBUG_REPORT_SAVE && console.debug(`Saving changes to report ${id}`)

          const body = {
            ..._.omit(report, ['_id', 'originalSettings']),
            settings: settingsStringified,
            ..._.omit(draftReportConfig ?? {}, reportSettingsPayloadOmitFields),
            modifiedDate: new Date(),
            creator: reportUserId,
            version: report.version
          } as Partial<ICatalogReportEs>

          const r = await es.genericUpsert({
            index: es.indexes.reports,
            _id: id,
            refresh,
            body
          })

          runInAction(() => {
            let original = DEBUG_REPORT_SAVE ? _.cloneDeep(this.report) : null
            if (refresh) {
              _.assign(this.report, _.omit(refresh ? r.data : body, ['settings']))
            }

            // this.originalSettings = removeUndefinedsNested(_.cloneDeep(settings))

            store.updateReportQueryParams()
            DEBUG_REPORT_SAVE && console.debug(`Settings updated in ES for report ${r.data.id} - draft: ${isDraft}`, {responseDataSubjectToRefresh: r.data, refresh, draftReportConfig, delta: diff(original, report)})
            this.saving = false
          })

          return r.data
        } else {
          throw new Error(`Shouldn't be here - you should be forced into a draft report or be updating your draft report`)
        }
      }
    } finally {
      this.saving = false
      this.isDirty = false
      this.store.loadRecentReports()
    }
  }

  @observable isDirty = false

  @computed get reportSettingsParsed() {
    const {report: {settings}} = this
    return settings ? JSON.parse(settings) : null
  }

  /**
   * Changes to this will cause the flex layout model to reload
   */
  @computed get settingsParsed(): ReportCatalogSerializableSettings {
    const {store: {currentReportModel, currentReport}} = this
    try {
      if (!currentReportModel) {
        return this.reportSettingsParsed
      } else {
        const {settingsForHistoryIndex: settings} = currentReportModel

        return settings ? _.merge({}, this.reportSettingsParsed, {flexLayout: {model: JSON.parse(settings)}}) : this.reportSettingsParsed
      }
    } catch (err) {
      console.error(err)
      return {}
    }
  }

  @computed get displayName() {
    const {report} = this
    return report.title || ui.settings.reports.defaultName
  }

  /**
   * Update query parameters to select this report using either the user-defined slug or an id
   * @param r
   */
  @action navigateTo = async (openAsPublicReport?: boolean) => {
    const {store, queryParams, store: {flexLayout}} = this

    DEBUG_REPORT_SAVE && console.debug(`Navigate to report '${this.id}'`, toJS(queryParams))
    store.onReportNavigatedTo(this)
    store._loadingReport = true
    nav.updateQueryParams(queryParams)
    if (openAsPublicReport) {
      this.isLocalDraft = true
    }
    store.currentReport = this
    store.currentReportModel = null
    flexLayout.reset()
    await store.loadCatalogReportFromQueryParamsOrLocalStorage(openAsPublicReport)
    //reports.loadCatalogReportFromEs() // We already have it, no?
  }

  /**
   * The non-draft version of the report
   */
  @computed get originalReport(): ReportWrapper {
    const {report: {draftedFrom}, store: {reportById, getReport}} = this
    if (draftedFrom) {
      return reportById.get(draftedFrom.id)

    } else {
      return this
    }
  }

  @observable _drafts: Array<ReportWrapper> = null

  @action loadDrafts = async () => {
    const {isDraft, store: {es, reportUserId}, report: {id}} = this
    if (isDraft || !id) return
    const {items} = es.unwrapHitsResult<ICatalogReportEs>(
      await es.search({
        meta: `Load draft versions for report ${id}`,
        index: es.indexes.reports,
        body: {
          query: {
            bool: {
              filter: [
                {term: {'creator.keyword': reportUserId}},
                {term: {'draftedFrom.id.keyword': id}}
              ]
            }
          },
          size: 100
        }
      }, {log: {query: false}}))

    this._drafts = items.map(r => new ReportWrapper(r))
  }

  @computed get drafts() {
    const {report, _drafts} = this
    if (_drafts) return _drafts

    if (this.isDraft) return null

    setTimeout(() => {
      this.loadDrafts()
    }, 0)

    return null
  }

  @action toggleMakeReportMyDefault = () => {
    const {isMyDefaultReport, store, store: {stores: {currentUser: {userSettings}}}} = this
    userSettings.defaultReport = isMyDefaultReport ? null : this.id
  }

  @action makeDefaultReport = () => {
    this.store.stores.currentUser.userSettings.defaultReport = this.id
  }

  @computed get isMyDefaultReport() {
    return this.id === this.store.stores.currentUser?.userSettings?.defaultReport
  }

  @action saveLocalDraft = (settingsJson?: string) => {
    lockr.set(this.localStorageDraftId, settingsJson ?? this.report.settings)
    this.isLocalDraft = true
  }
}



// WEBPACK FOOTER //
// ./src/stores/reports/ReportWrapper.ts